import type { NativeStackNavigationOptions } from '@react-navigation/native-stack'
import { Platform } from 'react-native'
import type { MD3Theme } from 'react-native-paper'
import type { IconType } from '@/lib/types'

export interface RouteOptions extends NativeStackNavigationOptions {
    parentRoute?: string
    headerRightButton?: {
        icon: IconType
        label: string
        route: string
    }
}

export type RouteConfig = Record<string, RouteOptions>

export const routes = {
    index: {
        headerShown: false,
        title: 'Dashboard',
    },
    'entry/index': {
        title: 'Time Entry',
    },
    'monitor/index': {
        title: 'Monitor',
    },
    'monitor/[userId]/index': {
        title: 'Monitor',
        parentRoute: 'monitor/index',
    },
    'approval/index': {
        title: 'Approval',
    },
    'analytics/index': {
        title: 'Analytics',
    },
    'settings/jobs/index': {
        title: 'Jobs',
        parentRoute: 'settings/index',
        headerRightButton: {
            icon: 'plus',
            label: 'Add Job',
            route: 'settings/jobs/new',
        },
    },
    'settings/jobs/new': {
        title: 'New Job',
        parentRoute: 'settings/jobs/index',
    },
    'settings/jobs/[jobSlug]/index': {
        title: 'Job Details',
        parentRoute: 'settings/jobs/index',
    },
    'settings/jobs/[jobSlug]/edit': {
        title: 'Edit Job',
        parentRoute: 'settings/jobs/[jobSlug]/index',
        headerBackButtonMenuEnabled: false,
    },
    'settings/jobs/[jobSlug]/locations/index': {
        title: 'Locations',
        parentRoute: 'settings/jobs/[jobSlug]/index',
    },
    'settings/jobs/[jobSlug]/locations/new': {
        title: 'New Location',
        parentRoute: 'settings/jobs/[jobSlug]/index',
    },
    'settings/jobs/[jobSlug]/locations/[locationId]/edit': {
        title: 'Edit Location',
        parentRoute: 'settings/jobs/[jobSlug]/index',
        headerBackButtonMenuEnabled: false,
    },
    'settings/tags/index': {
        title: 'Tags',
        parentRoute: 'settings/index',
        headerRightButton: {
            icon: 'plus',
            label: 'Add Tag',
            route: 'settings/tags/new',
        },
    },
    'settings/tags/new': {
        title: 'New Tag',
        parentRoute: 'settings/tags/index',
    },
    'settings/tags/[tagId]/edit': {
        title: 'Edit Tag',
        parentRoute: 'settings/tags/index',
        headerBackButtonMenuEnabled: false,
    },
    'settings/tags/[tagId]/index': {
        title: 'Tag Details',
        parentRoute: 'settings/tags/index',
    },
    'settings/customers/index': {
        title: 'Customers',
        parentRoute: 'settings/index',
        headerRightButton: {
            icon: 'plus',
            label: 'Create',
            route: 'settings/customers/new',
        },
    },
    'settings/customers/new': {
        title: 'New Customer',
        parentRoute: 'settings/customers/index',
    },
    'settings/customers/[customerSlug]/index': {
        title: 'Details',
        parentRoute: 'settings/customers/index',
    },
    'settings/customers/[customerSlug]/edit': {
        title: 'Edit Customer',
        parentRoute: 'settings/customers/[customerSlug]/index',
        headerBackButtonMenuEnabled: false,
    },
    'settings/index': {
        title: 'Settings',
    },
    'settings/my-details': {
        title: 'My Details',
        parentRoute: 'settings/index',
        headerBackButtonMenuEnabled: false,
    },
    'settings/organization': {
        title: 'Organization',
        parentRoute: 'settings/index',
        headerBackButtonMenuEnabled: false,
    },
    'settings/billing/index': {
        title: 'Billing',
        parentRoute: 'settings/index',
    },
    'settings/team/index': {
        title: 'Team',
        parentRoute: 'settings/index',
    },
    'settings/team/[memberId]/index': {
        title: 'Member Details',
        parentRoute: 'settings/team/index',
        headerBackButtonMenuEnabled: false,
    },
    'settings/team/invite': {
        title: 'Invite Member',
        parentRoute: 'settings/team/index',
    },
    'settings/superuser/index': {
        title: 'Superuser Tools',
        parentRoute: 'settings/index',
    },
} as const satisfies RouteConfig

export type RouteName = keyof typeof routes

export type RouteValue = (typeof routes)[RouteName]

export function getStackScreenOptions(theme: MD3Theme): NativeStackNavigationOptions {
    return {
        headerStyle: {
            backgroundColor: theme.colors.surface,
        },
        contentStyle: {
            backgroundColor: theme.colors.background,
        },

        headerTitleAlign: 'center',
        headerTintColor: theme.colors.primary,
        headerTitleStyle: {
            color: theme.colors.onSurface,
        },

        headerBlurEffect: Platform.OS === 'ios' ? 'regular' : undefined,
        headerTransparent: Platform.OS === 'ios',

        animation: 'default',
    }
}
